import React, { useState, useCallback, useMemo } from 'react';
import DataGrid from '../../common/DataGrid';
import ViewToolbar from '../../common/ViewToolbar';
import { usePeriodFilter } from '../../../hooks/usePeriodFilter';
import {
  fetchPurchaseBySupplierSummary,
  fetchPurchaseBySupplierDetail,
} from '../../../services/reportService';

const currencyFormatter = (params) => {
  if (params.value == null) return '';
  return Number(params.value).toFixed(2);
};

const percentFormatter = (params) => {
  if (params.value == null) return '';
  return `${Number(params.value).toFixed(2)}%`;
};

const qtyFormatter = (params) => {
  if (params.value == null) return '';
  return Number(params.value).toLocaleString();
};

const dateFormatter = (params) => {
  if (!params.value) return '';
  return new Date(params.value).toLocaleDateString();
};

const summaryColumns = [
  { field: 'supplier_name', headerName: 'Supplier', flex: 2, minWidth: 200 },
  { field: 'supplier_contact', headerName: 'Contact', flex: 1, minWidth: 140 },
  { field: 'bill_count', headerName: 'Bills', type: 'rightAligned', minWidth: 80, flex: 0.5 },
  { field: 'total_qty', headerName: 'Qty', valueFormatter: qtyFormatter, type: 'rightAligned', minWidth: 90, flex: 0.6 },
  { field: 'total_purchase_excl', headerName: 'Purchases (Excl)', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 130, flex: 1 },
  { field: 'total_tax', headerName: 'Tax', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 100, flex: 0.7 },
  { field: 'total_purchase_incl', headerName: 'Purchases (Incl)', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 130, flex: 1 },
  { field: 'avg_unit_price', headerName: 'Avg Unit Price', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 120, flex: 0.8 },
];

const detailColumns = [
  { field: 'bill_date', headerName: 'Date', minWidth: 110, flex: 0.7, valueFormatter: dateFormatter },
  { field: 'bill_number', headerName: 'Invoice #', minWidth: 110, flex: 0.8 },
  { field: 'item_description', headerName: 'Description', minWidth: 180, flex: 1.5 },
  { field: 'category_name', headerName: 'Category', minWidth: 120, flex: 0.8 },
  { field: 'qty', headerName: 'Qty', valueFormatter: qtyFormatter, type: 'rightAligned', minWidth: 70, flex: 0.5 },
  { field: 'unit_cost_excl', headerName: 'Unit Cost', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 100, flex: 0.7 },
  { field: 'line_total_excl', headerName: 'Total (Excl)', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 110, flex: 0.8 },
  { field: 'line_tax', headerName: 'Tax', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 90, flex: 0.6 },
  { field: 'line_total_incl', headerName: 'Total (Incl)', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 110, flex: 0.8 },
  { field: 'current_selling_price_excl', headerName: 'Sell Price', valueFormatter: currencyFormatter, type: 'rightAligned', minWidth: 100, flex: 0.7 },
  { field: 'margin_pct', headerName: 'Margin %', valueFormatter: percentFormatter, type: 'rightAligned', minWidth: 90, flex: 0.6 },
];

export default function PurchaseBySupplierView() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gridApi, setGridApi] = useState(null);

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [detailRows, setDetailRows] = useState([]);

  const periodFilter = usePeriodFilter('this_month');

  const loadSummary = useCallback(async () => {
    const { dateFrom, dateTo } = periodFilter;
    if (!dateFrom || !dateTo) return;

    try {
      setLoading(true);
      setSelectedSupplier(null);
      setDetailRows([]);
      const data = await fetchPurchaseBySupplierSummary(dateFrom, dateTo);
      setRows(data);
    } catch (err) {
      console.error('Error fetching purchase by supplier:', err);
    } finally {
      setLoading(false);
    }
  }, [periodFilter]);

  const handleSupplierClick = useCallback(async (row) => {
    const { dateFrom, dateTo } = periodFilter;
    if (!dateFrom || !dateTo) return;

    try {
      setLoading(true);
      setSelectedSupplier(row);
      const data = await fetchPurchaseBySupplierDetail(dateFrom, dateTo, row.supplier_id);
      setDetailRows(data);
    } catch (err) {
      console.error('Error fetching supplier detail:', err);
    } finally {
      setLoading(false);
    }
  }, [periodFilter]);

  const handleBack = useCallback(() => {
    setSelectedSupplier(null);
    setDetailRows([]);
  }, []);

  const summaryTotals = useMemo(() => {
    if (!rows.length) return null;
    const totalQty = rows.reduce((s, r) => s + Number(r.total_qty || 0), 0);
    const totalPurchaseExcl = rows.reduce((s, r) => s + Number(r.total_purchase_excl || 0), 0);
    const totalTax = rows.reduce((s, r) => s + Number(r.total_tax || 0), 0);
    const totalPurchaseIncl = rows.reduce((s, r) => s + Number(r.total_purchase_incl || 0), 0);
    const billCount = rows.reduce((s, r) => s + Number(r.bill_count || 0), 0);
    return { totalQty, totalPurchaseExcl, totalTax, totalPurchaseIncl, billCount };
  }, [rows]);

  const detailTotals = useMemo(() => {
    if (!detailRows.length) return null;
    const totalQty = detailRows.reduce((s, r) => s + Number(r.qty || 0), 0);
    const totalPurchaseExcl = detailRows.reduce((s, r) => s + Number(r.line_total_excl || 0), 0);
    const totalTax = detailRows.reduce((s, r) => s + Number(r.line_tax || 0), 0);
    const totalPurchaseIncl = detailRows.reduce((s, r) => s + Number(r.line_total_incl || 0), 0);
    return { totalQty, totalPurchaseExcl, totalTax, totalPurchaseIncl, billCount: detailRows.length };
  }, [detailRows]);

  const activeTotals = selectedSupplier ? detailTotals : summaryTotals;

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4 flex flex-wrap gap-3 items-end">
        <ViewToolbar
          searchTerm=""
          onSearchChange={() => {}}
          placeholder="Search..."
          addLabel="Run Report"
          addColor="bg-blue-600 hover:bg-blue-700"
          onAddNew={loadSummary}
          onExportCsv={gridApi ? () => gridApi.exportToCsv() : undefined}
          period={periodFilter.period}
          onPeriodChange={periodFilter.onPeriodChange}
          dateFrom={periodFilter.dateFrom}
          onDateFromChange={periodFilter.onDateFromChange}
          dateTo={periodFilter.dateTo}
          onDateToChange={periodFilter.onDateToChange}
        />
      </div>

      {selectedSupplier && (
        <div className="mb-3 flex items-center gap-2 text-sm">
          <button
            onClick={handleBack}
            className="text-blue-600 hover:text-blue-800 font-medium cursor-pointer flex items-center gap-1"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            All Suppliers
          </button>
          <span className="text-gray-400">/</span>
          <span className="font-semibold text-gray-700">{selectedSupplier.supplier_name}</span>
        </div>
      )}

      {activeTotals && (
        <div className="mb-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <TotalCard label="Bills" value={activeTotals.billCount} isCurrency={false} isInteger />
          <TotalCard label="Qty" value={activeTotals.totalQty} isCurrency={false} isInteger />
          <TotalCard label="Purchases (Excl)" value={activeTotals.totalPurchaseExcl} />
          <TotalCard label="Tax" value={activeTotals.totalTax} />
          <TotalCard label="Purchases (Incl)" value={activeTotals.totalPurchaseIncl} />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center mt-10 text-gray-500">Loading...</div>
      ) : rows.length === 0 ? (
        <div className="flex justify-center mt-10 text-gray-400 text-sm">
          Select a period and click "Run Report" to view purchase data.
        </div>
      ) : (
        <div className="flex-1 min-h-0">
          {selectedSupplier ? (
            <DataGrid
              rowData={detailRows}
              columns={detailColumns}
              onGridReady={setGridApi}
              exportFileName="purchase-by-supplier-detail.csv"
              pagination
              paginationSize={20}
            />
          ) : (
            <DataGrid
              rowData={rows}
              columns={summaryColumns}
              onEdit={handleSupplierClick}
              onGridReady={setGridApi}
              exportFileName="purchase-by-supplier.csv"
              editLabel="View"
              pagination
              paginationSize={20}
            />
          )}
        </div>
      )}
    </div>
  );
}

function TotalCard({ label, value, suffix, isCurrency = true, isInteger = false }) {
  let formatted;
  if (isInteger) {
    formatted = Number(value || 0).toLocaleString();
  } else if (isCurrency) {
    formatted = Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else {
    formatted = Number(value || 0).toFixed(2);
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 shadow-sm">
      <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-lg font-semibold text-gray-800 mt-0.5">
        {isCurrency && '$'}{formatted}{suffix || ''}
      </div>
    </div>
  );
}
